import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import FormGroup from '../common/FormGroup';
import { WizardProgress, WizardSection } from '../wizard/WizardComponents';
import AddressForm from './AddressForm';
//import { validators, validateForm } from '../../utils/validation';
import { validators,validateForm } from '../../utils/Validation';

const RegistrationStep1 = ({ onNavigate, onLogout, onPhotoSelected  }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    dob: '',
    gender: 'Male',
    maritalStatus: '',
    religion: '',
    bloodGroup: '',
    nativeRegion: '',
    mobile: '',
    email: '',
    fatherName: '',
    fatherMobile: '',
    fatherEmail: '',
    motherName: '',
    motherMobile: '',
    motherEmail: '',
    localGuardianName: '',
    guardianRelation: '',
    guardianMobile: '',
    guardianEmail: '',
    guardianIdType: '',
    guardianIdNumber: '',
    aadhar: '',
    emergencyContact: '',
    drivingLicense: '',
    passport: '',
  });

const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  // Restore saved data when navigating back to Step 1
  useEffect(() => {
    // Only auto-fill mobile for STUDENT role — not for staff logins
    const currentUserRole = (() => { try { return JSON.parse(localStorage.getItem('user')||'{}')?.role?.toUpperCase(); } catch { return ''; } })();
    const isGuestLogin = currentUserRole === 'STUDENT' || currentUserRole === '';
    const guestMobile = isGuestLogin ? (localStorage.getItem('guestMobile') || sessionStorage.getItem('guestMobile')) : null;

    const saved = localStorage.getItem('registrationStep1Data');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(prev => ({ ...prev, ...parsed }));
      } catch (e) {}
    }

    // Always override mobile with guestMobile AFTER restoring saved data
    // so saved data never overwrites the login mobile
    if (guestMobile) {
      setFormData(prev => ({ ...prev, mobile: guestMobile }));
    }
    // Restore photo preview and file from sessionStorage (survives navigation)
    const savedPhotoData = sessionStorage.getItem('regPhotoData');
    if (savedPhotoData) {
      try {
        const { base64, name, type } = JSON.parse(savedPhotoData);
        setPhotoPreview(base64);
        // Reconstruct File from base64 so it's ready to upload
        const byteStr = atob(base64.split(',')[1]);
        const arr = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i);
        const file = new File([arr], name, { type });
        setPhotoFile(file);
      } catch(e) {}
    } else {
      const savedPreview = localStorage.getItem('registrationPhotoPreview');
      if (savedPreview) setPhotoPreview(savedPreview);
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
    // Clear parent required banner when user edits any parent field
    if (['fatherName','fatherMobile','motherName','motherMobile'].includes(field) && errors.parentRequired) {
      setErrors(prev => ({ ...prev, [field]: '', parentRequired: false }));
    }
  };

  const handleBlur = (field, validator) => {
    setTouched({ ...touched, [field]: true });
    if (validator) {
      const error = validator(formData[field]);
      setErrors({ ...errors, [field]: error });
    }
  };

  const handlePhotoUpload = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  
  if (!file.type.startsWith('image/')) {
    alert('Please select an image file (JPG, PNG, GIF)');
    return;
  }
  
  if (file.size > 2 * 1024 * 1024) {
    alert('File size must be less than 2MB');
    return;
  }
  
  setPhotoFile(file);
  const reader = new FileReader();
  reader.onloadend = () => {
    const base64 = reader.result;
    setPhotoPreview(base64);
    // Store base64 + metadata in sessionStorage so it survives step navigation
    sessionStorage.setItem('regPhotoData', JSON.stringify({
      base64,
      name: file.name,
      type: file.type,
    }));
  };
  reader.readAsDataURL(file);
};

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    // Validation rules for step 1
    const validationRules = {
      fullName: validators.name,
      dob: validators.dob,
      gender: validators.required,
      nativeRegion: validators.required,
      mobile: validators.mobile,
      email: validators.email,
      fatherName: validators.optionalCapitalFirst,
      fatherMobile: validators.optionalMobile,
      fatherEmail: validators.optionalEmail,
      motherName: validators.optionalCapitalFirst,
      motherMobile: validators.optionalMobile,
      motherEmail: validators.optionalEmail,
      guardianMobile: validators.optionalMobile,
      guardianEmail: validators.optionalEmail,
      aadhar: validators.aadhar,
      emergencyContact: validators.optionalMobile,
      // Address validations
      permAddress1: validators.address,
      permCity: validators.city,
      permZipcode: validators.pincode,
      permState: validators.city,
      permCountry: validators.city,
      corrAddress1: validators.address,
      corrCity: validators.city,
      corrZipcode: validators.pincode,
      corrState: validators.city,
      corrCountry: validators.city,
    };

    const { isValid: fieldsValid, errors: validationErrors } = validateForm(formData, validationRules);

    // At least one parent section must be complete (name + mobile both required)
    const fatherName   = formData.fatherName?.trim() || '';
    const fatherMobile = formData.fatherMobile?.trim() || '';
    const motherName   = formData.motherName?.trim() || '';
    const motherMobile = formData.motherMobile?.trim() || '';

    // ✅ Allow Indian (10 digit) or International (+countrycode) for parent complete check
    const isMobileValid = (m) => {
      const v = (m || '').trim().replace(/\s/g, '');
      return /^[6-9]\d{9}$/.test(v) || /^\+[1-9]\d{6,14}$/.test(v);
    };
    const fatherComplete = fatherName.length >= 2 && isMobileValid(fatherMobile);
    const motherComplete = motherName.length >= 2 && isMobileValid(motherMobile);

    if (!fatherComplete && !motherComplete) {
      validationErrors.parentRequired = true;
    }

    const isValid = fieldsValid && (fatherComplete || motherComplete);

    setErrors(validationErrors);
    setShowValidationSummary(!isValid);

    if (isValid) {
  // Save form data
  localStorage.setItem('registrationStep1Data', JSON.stringify(formData));

  // Save photo preview (for UI only)
  if (photoPreview) {
    localStorage.setItem('registrationPhotoPreview', photoPreview);
  }

  // We cannot store File object directly in localStorage
  // So we keep it in memory using sessionStorage flag
  // If photoFile is null but sessionStorage has it, reconstruct
  let photoToUpload = photoFile;
  if (!photoToUpload) {
    const saved = sessionStorage.getItem('regPhotoData');
    if (saved) {
      try {
        const { base64, name, type } = JSON.parse(saved);
        const byteStr = atob(base64.split(',')[1]);
        const arr = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i);
        photoToUpload = new File([arr], name, { type });
      } catch(e) {}
    }
  }
  window.selectedPhotoFile = photoToUpload;
  if (onPhotoSelected) onPhotoSelected(photoToUpload);  // ✅ persist in parent state (survives reload/navigation)

  onNavigate('registration2');
} else {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
  }

  // ✅ Real-time mobile availability check — runs on blur
  const checkMobileAvailability = async (mobile) => {
    if (!mobile || mobile.trim().length < 10) return;
    try {
      const api = (await import('../../services/api')).default;
      await api.get(`/auth/check-mobile?mobile=${encodeURIComponent(mobile)}`);
      // No error = mobile is available
    } catch (err) {
      const msg = err.response?.data?.message || '';
      if (msg.includes('already registered') || msg.includes('staff account')) {
        setErrors(prev => ({ ...prev, mobile: msg }));
        setTouched(prev => ({ ...prev, mobile: true }));
      }
    }
  };

  const handleInputRestriction = (field, value, type) => {
    // Restrict input based on type
    if (type === 'number') {
      // ✅ Allow + at start for international numbers, digits only otherwise
      if (value.startsWith('+')) {
        return '+' + value.slice(1).replace(/\D/g, '');
      }
      return value.replace(/\D/g, '');
    }
    if (type === 'name') {
      return value.replace(/[^a-zA-Z\s]/g, '');
    }
    return value;
  };

  // Auto-capitalise: strip non-alpha/space, capitalise first letter of each word
  const capitaliseWords = (value) => {
    const cleaned = value.replace(/[^a-zA-Z\s]/g, '');
    return cleaned.replace(/\b\w/g, (ch) => ch.toUpperCase());
  };

  // Capitalise first letter only
  const capitaliseFirst = (value) => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  return (
    <div className="app-layout">
      <Sidebar activeItem="registration" onNavigate={onNavigate} onLogout={onLogout} />
      
      <main className="main-content">
        <div className="top-bar">
          <h1 className="page-title">New Registration - Step 1 of 4</h1>
        </div>
        
        <div className="content-area">
          <div className="wizard-container">
            <WizardProgress currentStep={1} />
            
            <div className="wizard-card">
              {/* {showValidationSummary && Object.keys(errors).length > 0 && (
                <div className="validation-summary">
                  <div className="validation-summary-title">Please fix the following errors:</div>
                  <ul className="validation-summary-list">
                    {Object.entries(errors).map(([field, error]) => 
                      error && <li key={field}>{error}</li>
                    )}
                  </ul>
                </div>
              )} */}

              <WizardSection 
                icon="👤" 
                title="Personal Information" 
                subtitle="Basic details and contact information"
              >
                <div className="form-grid">
                   <div className="photo-upload">
                    <div className="upload-preview">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Profile" style={{
                          width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px'
                        }} />
                      ) : (
                        <span>📷</span>
                      )}
                    </div>
                    <input
                      type="file"
                      id="photo-upload-input"
                      accept=".jpg,.jpeg,.png"
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="photo-upload-input" className="btn-secondary" style={{ cursor: 'pointer' }}>
                      {photoPreview ? 'Change Photo' : 'Upload Photo'}
                    </label>
                    {photoPreview && (
                      <button 
                        type="button" 
                        onClick={() => { setPhotoPreview(null); setPhotoFile(null); sessionStorage.removeItem('regPhotoData'); }}
                        style={{ 
                          marginTop: '8px', width: '100%', padding: '8px', 
                          background: '#e74c3c', color: 'white', border: 'none', 
                          borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                        }}
                      >
                        Remove Photo
                      </button>
                    )}
                  </div>
                  
                  <FormGroup label="Full Name *" error={touched.fullName && errors.fullName}>
                    <input 
                      type="text" 
                      className={`form-input ${errors.fullName ? 'form-input-error' : ''}`}
                      placeholder="Enter full name"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange('fullName', capitaliseWords(e.target.value))}
                      onBlur={() => handleBlur('fullName', validators.name)}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Date of Birth *" error={touched.dob && errors.dob}>
                    <input 
                      type="date" 
                      className={`form-input ${errors.dob ? 'form-input-error' : ''}`}
                      value={formData.dob}
                      onChange={(e) => handleInputChange('dob', e.target.value)}
                      onBlur={() => handleBlur('dob', validators.dob)}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Gender *" error={touched.gender && errors.gender}>
                    <input
                      className="form-input"
                      value="Male"
                      readOnly
                      style={{ background: '#f5f5f5', cursor: 'not-allowed', color: '#2C2C2C' }}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Marital Status">
                    <select 
                      className="form-input"
                      value={formData.maritalStatus}
                      onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                    >
                      <option value="">Select status</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                    </select>
                  </FormGroup>
                  
                  <FormGroup label="Religion">
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder="Enter religion"
                      value={formData.religion}
                      onChange={(e) => handleInputChange('religion', capitaliseWords(e.target.value))}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Blood Group" error={touched.bloodGroup && errors.bloodGroup}>
                    <select 
                      className={`form-input ${errors.bloodGroup ? 'form-input-error' : ''}`}
                      value={formData.bloodGroup}
                      onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                      onBlur={() => handleBlur('bloodGroup', validators.bloodGroup)}
                    >
                      <option value="">Select blood group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </FormGroup>
                  
                  <FormGroup label="Native Region *" error={touched.nativeRegion && errors.nativeRegion}>
                    <input 
                      type="text" 
                      className={`form-input ${errors.nativeRegion ? 'form-input-error' : ''}`}
                      placeholder="Enter region (e.g. Pune)"
                      value={formData.nativeRegion}
                      onChange={(e) => handleInputChange('nativeRegion', capitaliseWords(e.target.value))}
                      onBlur={() => handleBlur('nativeRegion', validators.required)}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Mobile Number *" error={touched.mobile && errors.mobile}>
                    {(() => {
                      const isGuestMobile = !!(localStorage.getItem('guestMobile') || sessionStorage.getItem('guestMobile'));
                      return (
                        <>
                          <input
                            type="tel"
                            className={`form-input ${errors.mobile ? 'form-input-error' : ''}`}
                            placeholder="10-digit Indian or +CountryCode (e.g. +44 20 7946 0958)"
                            value={formData.mobile}
                            onChange={isGuestMobile ? undefined : (e) => {
                              // ✅ Allow + at start, digits and spaces for international numbers
                              let val = e.target.value;
                              if (val.startsWith('+')) {
                                val = '+' + val.slice(1).replace(/[^0-9\s]/g, '');
                              } else {
                                val = val.replace(/\D/g, '');
                              }
                              handleInputChange('mobile', val);
                            }}
                            onBlur={async () => {
                              handleBlur('mobile', validators.mobile);
                              // ✅ Check if mobile already registered
                              if (formData.mobile && !errors.mobile) {
                                await checkMobileAvailability(formData.mobile);
                              }
                            }}
                            maxLength="18"
                            readOnly={isGuestMobile}
                            style={isGuestMobile ? { background:'#F8F6F3', cursor:'not-allowed', borderColor:'#4CAF50', color:'#1B3B5F', fontWeight:600 } : {}}
                          />
                          {isGuestMobile && (
                            <div style={{ fontSize:11, color:'#2D5F4F', marginTop:4, fontWeight:600 }}>
                              ✓ Auto-filled from your login — cannot be changed
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </FormGroup>
                  
                  <FormGroup label="Student Email ID *" error={touched.email && errors.email}>
                    <input 
                      type="email" 
                      className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={() => handleBlur('email', validators.email)}
                    />
                  </FormGroup>
                </div>
              </WizardSection>
              
              <WizardSection 
                icon="👨‍👩‍👦" 
                title="Family Details" 
                subtitle="Parents and guardian information"
              >
                {errors.parentRequired && (
                  <div style={{
                    margin: '0 0 18px 0',
                    padding: '12px 16px',
                    background: '#FFF3F3',
                    border: '1px solid #E85D4A',
                    borderLeft: '4px solid #E85D4A',
                    borderRadius: 4,
                    color: '#9e2a2a',
                    fontSize: 14,
                    fontWeight: 500,
                  }}>
                    ⚠️ Please fill at least one parent's details — either Father or Mother (Name & Mobile are required)
                  </div>
                )}
                <div className="form-grid">
                  <FormGroup label="Father's Name" error={touched.fatherName && errors.fatherName}>
                    <input 
                      type="text" 
                      className={`form-input ${errors.fatherName ? 'form-input-error' : ''}`}
                      placeholder="Enter father's name"
                      value={formData.fatherName}
                      onChange={(e) => handleInputChange('fatherName', capitaliseWords(e.target.value))}
                      onBlur={() => handleBlur('fatherName', validators.optionalCapitalFirst)}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Father's Mobile No." error={touched.fatherMobile && errors.fatherMobile}>
                    <input 
                      type="tel" 
                      className={`form-input ${errors.fatherMobile ? 'form-input-error' : ''}`}
                      placeholder="10-digit or international (e.g. +44 20 7946 0958)"
                      value={formData.fatherMobile}
                      onChange={(e) => { let val = e.target.value; if (val.startsWith('+')) { val = '+' + val.slice(1).replace(/[^0-9 ]/g, ''); } else { val = val.replace(/\D/g, ''); } handleInputChange('fatherMobile', val); }}
                      onBlur={() => handleBlur('fatherMobile', validators.optionalMobile)}
                      maxLength="18"
                    />
                  </FormGroup>
                  
                  <FormGroup label="Father's Email ID" error={touched.fatherEmail && errors.fatherEmail}>
                    <input 
                      type="email" 
                      className={`form-input ${errors.fatherEmail ? 'form-input-error' : ''}`}
                      placeholder="Enter email address"
                      value={formData.fatherEmail}
                      onChange={(e) => handleInputChange('fatherEmail', e.target.value)}
                      onBlur={() => handleBlur('fatherEmail', validators.optionalEmail)}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Mother's Name" error={touched.motherName && errors.motherName}>
                    <input 
                      type="text" 
                      className={`form-input ${errors.motherName ? 'form-input-error' : ''}`}
                      placeholder="Enter mother's name"
                      value={formData.motherName}
                      onChange={(e) => handleInputChange('motherName', capitaliseWords(e.target.value))}
                      onBlur={() => handleBlur('motherName', validators.optionalCapitalFirst)}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Mother's Mobile No." error={touched.motherMobile && errors.motherMobile}>
                    <input 
                      type="tel" 
                      className={`form-input ${errors.motherMobile ? 'form-input-error' : ''}`}
                      placeholder="10-digit or international (e.g. +44 20 7946 0958)"
                      value={formData.motherMobile}
                      onChange={(e) => { let val = e.target.value; if (val.startsWith('+')) { val = '+' + val.slice(1).replace(/[^0-9 ]/g, ''); } else { val = val.replace(/\D/g, ''); } handleInputChange('motherMobile', val); }}
                      onBlur={() => handleBlur('motherMobile', validators.optionalMobile)}
                      maxLength="18"
                    />
                  </FormGroup>
                  
                  <FormGroup label="Mother's Email ID" error={touched.motherEmail && errors.motherEmail}>
                    <input 
                      type="email" 
                      className={`form-input ${errors.motherEmail ? 'form-input-error' : ''}`}
                      placeholder="Enter email address"
                      value={formData.motherEmail}
                      onChange={(e) => handleInputChange('motherEmail', e.target.value)}
                      onBlur={() => handleBlur('motherEmail', validators.optionalEmail)}
                    />
                  </FormGroup>
                </div>
              </WizardSection>
              
              <WizardSection 
                icon="📍" 
                title="Address Information" 
                subtitle="Permanent and correspondence addresses"
              >
                <AddressForm 
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  touched={touched}
                  setTouched={setTouched}
                />
              </WizardSection>
              
              <WizardSection 
                icon="🆔" 
                title="Guardian & ID Proof Details" 
                subtitle="Local guardian and identification information"
              >
                <div className="form-grid">
                  <FormGroup label="Local Guardian's Name" error={touched.localGuardianName && errors.localGuardianName}>
                    <input 
                      type="text" 
                      className={`form-input ${errors.localGuardianName ? 'form-input-error' : ''}`}
                      placeholder="Enter guardian name" 
                      value={formData.localGuardianName}
                      onChange={(e) => handleInputChange('localGuardianName', capitaliseWords(e.target.value))}
                      onBlur={() => handleBlur('localGuardianName', validators.optionalCapitalFirst)}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Relation" error={touched.guardianRelation && errors.guardianRelation}>
                    <input 
                      type="text" 
                      className={`form-input ${errors.guardianRelation ? 'form-input-error' : ''}`}
                      placeholder="Relationship (e.g. Uncle)" 
                      value={formData.guardianRelation}
                      onChange={(e) => handleInputChange('guardianRelation', capitaliseWords(e.target.value))}
                      onBlur={() => handleBlur('guardianRelation', validators.optionalCapitalFirst)}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Guardian's Mobile No." error={touched.guardianMobile && errors.guardianMobile}>
                    <input 
                      type="tel" 
                      className={`form-input ${errors.guardianMobile ? 'form-input-error' : ''}`}
                      placeholder="10-digit or +CountryCode (e.g. +1XXXXXXXXXX)" 
                      value={formData.guardianMobile}
                      onChange={(e) => { let val = e.target.value; if (val.startsWith('+')) { val = '+' + val.slice(1).replace(/[^0-9 ]/g, ''); } else { val = val.replace(/\D/g, ''); } handleInputChange('guardianMobile', val); }}
                      onBlur={() => handleBlur('guardianMobile', validators.optionalMobile)}
                      maxLength="18"
                    />
                  </FormGroup>
                  
                  <FormGroup label="Guardian's Email ID" error={touched.guardianEmail && errors.guardianEmail}>
                    <input 
                      type="email" 
                      className={`form-input ${errors.guardianEmail ? 'form-input-error' : ''}`}
                      placeholder="Enter email address" 
                      value={formData.guardianEmail}
                      onChange={(e) => handleInputChange('guardianEmail', e.target.value)}
                      onBlur={() => handleBlur('guardianEmail', validators.optionalEmail)}
                    />
                  </FormGroup>
                  
                  <FormGroup label="Emergency Contact No." error={touched.emergencyContact && errors.emergencyContact}>
                    <input 
                      type="tel" 
                      className={`form-input ${errors.emergencyContact ? 'form-input-error' : ''}`}
                      placeholder="10-digit or +CountryCode (e.g. +1XXXXXXXXXX)" 
                      value={formData.emergencyContact}
                      onChange={(e) => { let val = e.target.value; if (val.startsWith('+')) { val = '+' + val.slice(1).replace(/[^0-9 ]/g, ''); } else { val = val.replace(/\D/g, ''); } handleInputChange('emergencyContact', val); }}
                      onBlur={() => handleBlur('emergencyContact', validators.optionalMobile)}
                      maxLength="18"
                    />
                  </FormGroup>

                  <FormGroup label="Guardian's ID Proof Type" error={touched.guardianIdType && errors.guardianIdType}>
                    <select
                      className={`form-input ${errors.guardianIdType ? 'form-input-error' : ''}`}
                      value={formData.guardianIdType}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, guardianIdType: val, guardianIdNumber: '' }));
                      }}
                    >
                      <option value="">Select ID type...</option>
                      <option value="Aadhar">Aadhar Card</option>
                      <option value="DrivingLicense">Driving License</option>
                      <option value="Passport">Passport</option>
                    </select>
                  </FormGroup>

                  {formData.guardianIdType && (
                    <FormGroup label="Guardian's ID Number" error={touched.guardianIdNumber && errors.guardianIdNumber}>
                      <input
                        type="text"
                        className={`form-input ${touched.guardianIdNumber && errors.guardianIdNumber ? 'form-input-error' : ''}`}
                        placeholder={
                          formData.guardianIdType === 'Aadhar' ? 'Enter 12-digit Aadhar number' :
                          formData.guardianIdType === 'DrivingLicense' ? 'e.g. MH1220150012345' :
                          formData.guardianIdType === 'Passport' ? 'e.g. A1234567' : 'Enter ID number'
                        }
                        value={formData.guardianIdNumber}
                        onChange={(e) => handleInputChange('guardianIdNumber',
                          formData.guardianIdType === 'Aadhar'
                            ? e.target.value.replace(/\D/g, '').slice(0, 12)
                            : e.target.value.toUpperCase()
                        )}
                        onBlur={() => {
                          const err = validators.guardianIdNumber(formData.guardianIdNumber, formData.guardianIdType);
                          setErrors(prev => ({ ...prev, guardianIdNumber: err }));
                          setTouched(prev => ({ ...prev, guardianIdNumber: true }));
                        }}
                        maxLength={formData.guardianIdType === 'Aadhar' ? 12 : formData.guardianIdType === 'Passport' ? 8 : 20}
                      />
                    </FormGroup>
                  )}

                </div>
              </WizardSection>
              
              <div className="wizard-actions">
                <button className="btn-back" onClick={() => onNavigate('dashboard')}>Cancel</button>
                <div className="btn-group">
                  <button className="btn-next" onClick={handleSubmit}>
                    Save & Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegistrationStep1;